import type { ComponentPropsWithoutRef, ForwardedRef } from "react";
import { Slot } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { forwardRef } from "@/shared/utils";

const buttonVariance = tv({
  base: "flex h-fit shrink-0 items-center justify-center gap-2 truncate rounded-lg border border-transparent py-2.5 font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-hidden disabled:cursor-not-allowed",
  variants: {
    variant: {
      primary:
        "bg-primary-950 text-white hover:bg-primary-900 focus-visible:ring-secondary-200 active:bg-primary-800 disabled:bg-neutral-700",
      secondary:
        "border-primary-950 bg-transparent text-primary-950 hover:border-primary-900 hover:text-primary-900 focus-visible:ring-secondary-200 active:border-primary-800 active:text-primary-800 disabled:border-neutral-400 disabled:text-neutral-400",
      "secondary-dark":
        "border-primary-200 bg-transparent text-white hover:border-secondary-100 hover:bg-primary-900 hover:text-secondary-100 focus-visible:ring-secondary-200 active:border-secondary-300 active:bg-primary-800 disabled:border-neutral-400 disabled:text-neutral-400",
      tertiary:
        "bg-transparent text-primary-950 outline-offset-2 hover:text-primary-900 hover:underline focus-visible:ring-secondary-200 active:text-primary-800 active:decoration-primary-800 disabled:text-neutral-700 disabled:hover:no-underline",
      "tertiary-dark":
        "text-white hover:bg-primary-900 hover:text-secondary-100 hover:underline focus-visible:ring-secondary-200 active:text-primary-200 disabled:text-neutral-700 disabled:hover:no-underline",
    },
    size: {
      xs: "rounded-sm px-2 py-1 text-xs",
      sm: "p-2 text-xs",
      lg: "px-6 text-base",
    },
    color: {
      red: "",
      brown: "",
      salmon: "",
    },
  },

  compoundVariants: [
    {
      variant: "secondary",
      color: "red",
      className:
        "border-red-800 text-red-800 hover:border-red-700 hover:text-red-700 focus-visible:ring-red-400 active:border-red-600 active:text-red-600",
    },
    {
      variant: "tertiary",
      color: "red",
      className:
        "text-red-800 hover:text-red-700 focus-visible:text-red-600 active:text-red-600",
    },
  ],

  defaultVariants: {
    size: "sm",
    variant: "primary",
  },
});
type ButtonVariance = VariantProps<typeof buttonVariance>;

export interface ButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "color">,
    ButtonVariance {
  asChild?: boolean;
}

export const Button = forwardRef(
  (
    {
      type = "button",
      className,
      asChild,
      variant,
      size,
      color,
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const Component = asChild ? Slot.Root : "button";

    return (
      <Component
        ref={ref}
        type={type}
        className={buttonVariance({ variant, size, color, className })}
        {...props}
      />
    );
  },
);
